import { focusOutline } from '@monorepo/shared/styles/focus';
import { bodySmall } from '@monorepo/shared/styles/text';
import React, { useRef } from 'react';
import styled from 'styled-components';

const StyledTabList = styled.div`
  display: flex;
  flex-shrink: 0;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.colors.grayf4};
  border-top: 1px solid ${({ theme }) => theme.colors.grayddd};
`;

const StyledTab = styled.button<{ $isSelected: boolean }>`
  ${bodySmall}
  flex-shrink: 0;
  padding: 0.375rem 0.75rem;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.blue : theme.colors.gray666};
  white-space: nowrap;
  cursor: pointer;
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.white : 'transparent'};
  border: none;
  border-right: 1px solid ${({ theme }) => theme.colors.grayddd};
  border-bottom: 2px solid
    ${({ theme, $isSelected }) =>
      $isSelected ? theme.colors.blue : 'transparent'};

  &:focus-visible {
    ${focusOutline({ $outlineOffset: '-0.125rem' })}
  }
`;

type SheetTabsProps = {
  idPrefix: string;
  sheetNames: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
};

/** Excel-style sheet tabs following the WAI-ARIA tabs pattern (arrow keys, Home, End). */
export function SheetTabs({
  idPrefix,
  sheetNames,
  selectedIndex,
  onSelect,
}: SheetTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectAndFocus = (index: number) => {
    onSelect(index);
    tabRefs.current[index]?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const lastIndex = sheetNames.length - 1;
    const nextIndexByKey: Record<string, number> = {
      ArrowRight: selectedIndex === lastIndex ? 0 : selectedIndex + 1,
      ArrowLeft: selectedIndex === 0 ? lastIndex : selectedIndex - 1,
      Home: 0,
      End: lastIndex,
    };
    const nextIndex = nextIndexByKey[event.key];
    if (nextIndex === undefined) return;
    event.preventDefault();
    selectAndFocus(nextIndex);
  };

  return (
    <StyledTabList role="tablist" aria-label="Sheets" onKeyDown={handleKeyDown}>
      {sheetNames.map((name, index) => {
        const isSelected = index === selectedIndex;
        return (
          <StyledTab
            key={name}
            ref={(element) => {
              tabRefs.current[index] = element;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${index}`}
            aria-selected={isSelected}
            aria-controls={`${idPrefix}-panel`}
            tabIndex={isSelected ? 0 : -1}
            $isSelected={isSelected}
            onClick={() => onSelect(index)}
          >
            {name}
          </StyledTab>
        );
      })}
    </StyledTabList>
  );
}
